import { inject, Injectable } from '@angular/core';
import { MatrixService } from '../matrix/matrix.service';
import { encryptedFileMetadata, EncryptResult, FileService } from '../file/file.service';
import { BehaviorSubject, firstValueFrom, Observable, throwError } from 'rxjs';
import { EncryptedFile, ImageInfo, MediaEventContent } from 'matrix-js-sdk/lib/@types/media';
import { decode, encode } from 'blurhash';
import { environment } from '../../../environments/environment';
import { SnackbarService } from '../snackbar/snackbar.service';
import { Room } from '../../../shared-api/room';
import { MatrixError, MatrixEvent, MsgType, UploadResponse } from 'matrix-js-sdk';
import encrypt from 'matrix-encrypt-attachment';
import { Message } from '../../../shared-api/message';
import { DisplayMessageFile } from '../../components/sb-client/sb-model/display-message-file';
import { MessageContent } from '../../../shared-api/messageContent';
import { MessageService } from '../message/message.service';
import { KnownMembership } from 'matrix-js-sdk/lib/@types/membership.js';
import { DialogData } from '../../common/components/dialog/dialog.component';
import { DialogService } from '../dialog/dialog.service';
import { RoomHelperService } from '../room/room-helper.service';
import { MediaFileId } from '../../../shared-api/mediaFileId';
import { MediaService as ApiMediaService } from '../../../shared-api/media.service';
import { matrixErrorAsJson } from '../../errors/error-with-status';
import { MediaTestIds } from './media.test-ids';

@Injectable({
  providedIn: 'root'
})
/**
 * Der MediaService stellt das Hoch- und Herunterladen von Medien bereit.
 * Implementation of the mandatory Media API of the gematik TI-M Testtreiber
 * Umgesetze AFOs:
 * A_25363 - Testtreiber-Modul
 * A_25361 - keine Modifikation der Inhalte
 */
export class MediaService implements ApiMediaService {
  private readonly file = inject(FileService);
  private readonly blobCache = new Map<string, Blob>();
  private readonly matrix = inject(MatrixService);
  private readonly MAX_WIDTH = 800;
  private readonly MAX_HEIGHT = 640;
  private readonly userAgentHeader: string = `${environment.timClientVersion}, ${navigator.userAgent}`;

  private readonly snackbarService = inject(SnackbarService);
  private readonly messageService = inject(MessageService);
  private readonly roomHelperService = inject(RoomHelperService);
  private readonly dialogService = inject(DialogService);

  private readonly isUploadEnabledSubject = new BehaviorSubject<boolean>(false);
  private readonly fileStatusSubjects = new Map<
    string,
    BehaviorSubject<{
      status: 'active' | 'inactive';
      fileName: string;
    }>
  >();

  /**
   * Returns a previously uploaded file with authentication
   * Since Matrix version 1.11 the download of media files is authenticated. This endpoint is used to test the new authenticated download, where fileId is the id received during upload.
   * @param fileId ID of media file
   * @returns A Blob containing the requested file
   * @throws Error if fileId is null or undefined
   */
  public async getAuthenticatedMediaFile(fileId: string): Promise<Blob> {
    if (!fileId) {
      throw new Error('Required parameter fileId was null or undefined when calling getAuthenticatedMediaFile.');
    }
    const blob = this.file.mediaStorage.get(fileId);
    if (!blob) {
      throw new Error(`no media file found for file id ${fileId}`);
    }

    return blob;
  }

  /**
   * Returns a previously uploaded file with given id
   * @param fileId ID of media file
   * @returns A Blob containing the requested file
   * @throws Error if fileId is null or undefined
   */
  public async getMediaFile(fileId: string): Promise<Blob> {
    return this.getAuthenticatedMediaFile(fileId);
  }

  /**
   * Testtreiber-Endpunkt POST /media: nimmt eine Datei entgegen und gibt eine fileId zurück.
   * Es findet KEIN Server-Upload statt - das Blob wird lokal in mediaStorage gehalten und
   * erst beim Senden (prepareMessageContent) hochgeladen. Die fileId ist ein opaker,
   * WAF-sicherer Schlüssel (SHA-256-Hex), keine Matrix content_uri.
   * @param contentType Define type of the sent media. Should be in compliance with RFC6838
   * @param body the file content as a blob
   * @returns An object containing the local fileId
   * @throws Error if contentType or body is null or undefined
   */
  public async postMediaFile(contentType: string, body: Blob): Promise<MediaFileId> {
    if (!contentType) {
      throw new Error('Required parameter contentType was null or undefined when calling postMediaFile.');
    }
    if (!body) {
      throw new Error('Required parameter body was null or undefined when calling postMediaFile.');
    }
    if (body.size === 0) {
      throw new Error('Empty file upload is not allowed.');
    }

    // Kein Server-Upload: postMediaFile ist eine treiber-interne Staging-Operation. Das Blob
    // wird lokal in mediaStorage gehalten und beim Senden (prepareMessageContent) hochgeladen
    // - genau wie im App-Pfad. Der fileId ist nur ein opaker Schlüssel in den mediaStorage
    // (siehe getAuthenticatedMediaFile). Er muss WAF-sicher sein (keine '/' oder ':'), da der
    // Testtreiber ihn bei getMediaFile/getAuthenticatedMediaFile als URL-Pfadsegment nutzt;
    // ein SHA-256-Hex erfüllt das und ist zugleich deterministisch und kollisionsfrei.
    const fileId = await this.computeLocalFileId(body);
    this.file.mediaStorage.set(fileId, body);

    return { fileId };
  }

  private async computeLocalFileId(blob: Blob): Promise<string> {
    const digest = await crypto.subtle.digest('SHA-256', await blob.arrayBuffer());
    return Array.from(new Uint8Array(digest))
      .map(byte => byte.toString(16).padStart(2, '0'))
      .join('');
  }

  blob2dataUrl(blob: Blob): Promise<string> {
    return new Promise(resolve => {
      const reader = new FileReader();
      reader.onload = (): void => resolve(reader.result as string);
      reader.readAsDataURL(blob);
    });
  }

  async dataUrl2blob(dataUrl: string): Promise<Blob> {
    return (await fetch(dataUrl)).blob();
  }

  /**
   * Decrypts the encrypted files from a list of Matrix events.
   * Caches decrypted blobs based on event ID to avoid redundant decryption.
   * Returns an array mapping each event to its corresponding decrypted Blob.
   * @param messageEvents Array of Matrix events possibly containing encrypted files
   * @returns An array of objects each containing the original event and the decrypted file as Blob
   */

  public async decryptMessageFiles(messageEvents: MatrixEvent[]): Promise<{ event: MatrixEvent; blob: Blob }[]> {
    const results: { event: MatrixEvent; blob: Blob }[] = [];

    for (const event of messageEvents) {
      const content = event.getContent();
      const eventId = event.getId();

      if (!eventId) {
        results.push({ event, blob: new Blob() });
        continue;
      }

      const cachedBlob = this.blobCache.get(eventId);
      if (cachedBlob && cachedBlob.size > 0) {
        results.push({ event, blob: cachedBlob });
        continue;
      }

      if (event.isEncrypted?.() && content['file']) {
        try {
          const blob = await this.file.decryptFile(content);
          this.blobCache.set(eventId, blob);
          results.push({ event, blob });
        } catch (error) {
          console.error(`Error decrypting file: ${eventId}:`, error);
          results.push({ event, blob: new Blob() });
        }
      } else {
        results.push({ event, blob: new Blob() });
      }
    }
    return results;
  }

  /**
   * Prepares media content by extracting file and thumbnail info.
   */
  public prepareMediaContent(
    content: Partial<{
      mxc: string;
      file?: EncryptedFile;
      thumbnail_url?: string;
      thumbnail_file?: EncryptedFile;
    }>
  ): IPreparedMedia {
    try {
      let thumbnail: IMediaObject | undefined;
      if (content.thumbnail_url && content.thumbnail_url.length > 0) {
        thumbnail = {
          mxc: content.thumbnail_url,
          file: content.thumbnail_file
        };
      }

      if (content.mxc) {
        return {
          mxc: content.mxc,
          file: content.file,
          thumbnail
        };
      }
    } catch (error) {
      console.error('Error preparing media content:', error);
    }
    return { mxc: '', file: undefined };
  }

  public async decryptMessageFile(messageFile: DisplayMessageFile): Promise<{ blob: Blob; isValid: boolean }> {
    const decryptedBlob = await this.file.decryptFile(messageFile.content);
    if (!messageFile.eventId) {
      throw new Error('MessageFile has no EventId, when calling decryptMessageFile');
    }
    this.blobCache.set(messageFile.eventId, decryptedBlob);
    const isBlobValid = await this.validateDecryptedBlob(decryptedBlob, messageFile.mimetype ?? '');
    return { blob: decryptedBlob, isValid: isBlobValid };
  }

  public async readFileAsArrayBuffer(file: File): Promise<ArrayBuffer> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (): void => {
        if (reader.result instanceof ArrayBuffer) {
          resolve(reader.result);
        } else {
          reject(new Error('Unerwarteter Ergebnistyp: Erwartet wurde ein ArrayBuffer'));
        }
      };

      reader.onerror = (): void => reject(reader.error ?? new Error('Unbekannter Fehler beim FileReader'));

      reader.readAsArrayBuffer(file);
    });
  }

  public async infoForImageFile(imageFile: File): Promise<ImageInfo> {
    try {
      const imageElement = await this.loadImageElement(imageFile);
      const iThumbnail = await this.createThumbnail(
        imageElement.img,
        imageElement.width,
        imageElement.height,
        imageFile.type
      );
      return iThumbnail.info;
    } catch (err) {
      console.error('Fehler beim Erstellen der Bildinformationen:', err);
      throw new Error('Error when trying to upload Picture', { cause: err });
    }
  }

  public validateDecryptedFileMetadata(mimetype: string, filename: string): boolean {
    const name = filename.toLowerCase().trim();
    const parts = name.split('.');
    if (parts.length !== 2) {
      return false;
    }

    return this.file.isAllowedMimeType(mimetype);
  }

  /**
   * Anhand der mxcUrl werden in dieser Methode die Metadaten des Files geholt der zugehörigen mxcUrl geholt
   * @param mxcUrl
   * Erfüllt die Afo A_25524
   */
  public async fetchMetadataOnly(mxcUrl: string): Promise<Headers> {
    const url = this.matrix.client.mxcUrlToHttp(mxcUrl, undefined, undefined, undefined, false, true);
    if (!url) {
      console.warn('Ungültige MXC-URL');
      return new Headers();
    }

    const requestInit: RequestInit = {
      method: 'GET',
      headers: {
        Range: 'bytes=0-0',
        'X-TIM-User-Agent': this.userAgentHeader
      }
    };
    try {
      const response = await fetch(url, requestInit);
      return response.headers;
    } catch (err) {
      console.error('Fehler beim Abrufen der Metadaten:', err);
      return new Headers();
    }
  }

  public async validateDecryptedBlob(blob: Blob, mimetype: string): Promise<boolean> {
    if (!this.file.isAllowedMimeType(mimetype)) {
      return false;
    }
    const buffer = await blob.slice(0, 32).arrayBuffer();
    const fileBytes = new Uint8Array(buffer);

    return this.file.checkFileSignature(fileBytes, mimetype);
  }

  public formatFileSize(sizeInBytes: number | undefined): string {
    if (!sizeInBytes || sizeInBytes === 0) return '0B';
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(sizeInBytes) / Math.log(1024));
    return `${(sizeInBytes / Math.pow(1024, i)).toFixed(1)} ${units[i]}`;
  }

  isUploadEnabled(): Observable<boolean> {
    return this.isUploadEnabledSubject.asObservable();
  }

  setUploadEnabled(isEnabled: boolean): void {
    return this.isUploadEnabledSubject.next(isEnabled);
  }

  getFileStatus(fileName: string): Observable<{
    status: 'active' | 'inactive';
    fileName: string;
  }> {
    if (!this.fileStatusSubjects.has(fileName)) {
      this.fileStatusSubjects.set(
        fileName,
        new BehaviorSubject<{
          status: 'active' | 'inactive';
          fileName: string;
        }>({
          status: 'inactive',
          fileName: fileName
        })
      );
    }
    return this.fileStatusSubjects.get(fileName)!.asObservable();
  }

  async getAndCheckAvatarFileOfEvent(event: Event): Promise<File | null> {
    const selectedFile: File | null = this.getFileOfEvent(event);
    await this.checkImageFile(selectedFile);
    return selectedFile;
  }

  async uploadFile(event: Event, activeRoom: Room): Promise<void | { encryptedFile: EncryptedFile }> {
    if (!activeRoom) {
      this.snackbarService.error('Kein Raum ausgewählt');
      return;
    }

    if (this.roomHelperService.getRoomMembership(activeRoom.roomId) === KnownMembership.Leave) {
      this.snackbarService.error('Sie können keine Dateien in einen Raum hochladen, den Sie verlassen haben.');
      return;
    }

    const selectedFile: File | null = this.getFileOfEvent(event);

    if (!selectedFile) {
      return;
    }
    const fileName = selectedFile.name;
    this.updateFileStatus(fileName, 'inactive');

    const isValid = await this.validateFile(selectedFile);
    if (!isValid) {
      this.updateFileStatus(fileName, 'inactive');
      return;
    }
    let inputMsgType: MsgType = MsgType.File;

    if (selectedFile.type.startsWith('image/')) {
      inputMsgType = MsgType.Image;
    } else if (selectedFile.type.startsWith('audio/')) {
      inputMsgType = MsgType.Audio;
    } else if (selectedFile.type.startsWith('video/')) {
      inputMsgType = MsgType.Video;
    }

    const content: MediaEventContent = {
      body: selectedFile.name,
      info: {
        size: selectedFile.size,
        mimetype: selectedFile.type
      },
      msgtype: inputMsgType
    };
    let startedUpload = false;

    try {
      const crypto = this.matrix.client.getCrypto();
      const isEncryptionEnabled = await (crypto?.isEncryptionEnabledInRoom(activeRoom.roomId ?? '') ?? false);
      startedUpload = true;
      return await this.uploadFileToServer(selectedFile, content, activeRoom.roomId, isEncryptionEnabled, true, true);
    } finally {
      if (startedUpload) {
        this.updateFileStatus(fileName, 'active');
      }
    }
  }

  async uploadAvatarToServer(file: File): Promise<UploadResponse> {
    await this.checkImageFile(file);
    return this.uploadContent(file);
  }

  public getFileOfEvent(event: Event): File | null {
    if (event instanceof DragEvent) {
      const dataTransfer = event.dataTransfer;
      if (dataTransfer) {
        return dataTransfer.files[0];
      }
    } else {
      const fileInput = event.target as HTMLInputElement;
      if (!fileInput.files || fileInput.files.length === 0) {
        console.warn('Keine Datei ausgewählt.');
        return null;
      }
      return fileInput.files[0];
    }
    return null;
  }

  async uploadFileToServer(
    file: File,
    content: MediaEventContent,
    activeRoomId: string,
    isEncryptionEnabled: boolean,
    isValid: boolean,
    sendAfterUpload: boolean
  ): Promise<{ encryptedFile: EncryptedFile } | void> {
    this.setUploadEnabled(true);

    if (isEncryptionEnabled) {
      return await this.uploadEncryptedFile(file, content, activeRoomId, isValid, sendAfterUpload);
    } else {
      return await this.uploadUnencryptedFile(file, content, activeRoomId, isValid, sendAfterUpload);
    }
  }

  public async validateFile(file: File): Promise<boolean> {
    if (!this.validateFileName(file.name)) {
      this.snackbarService.error('Ungültiger Dateiname');
      return false;
    }
    const isValidType = await this.file.validateFileType(file);
    if (!isValidType) {
      this.snackbarService.error('Ungültiger Dateityp');
      return false;
    }

    return await this.checkUploadSize(file);
  }

  decodeBlurhash(canvas: HTMLCanvasElement, blurhash: string, originalWidth: number, originalHeight: number): string {
    const maxSize = this.calculateMaxSize(originalWidth, originalHeight);
    if (blurhash.length < 6) {
      return '';
    }

    const pixels = decode(blurhash, maxSize.width, maxSize.height);

    canvas.width = maxSize.width;
    canvas.height = maxSize.height;
    const ctx = canvas.getContext('2d');

    if (!ctx) return '';

    // createImageData avoids TS ImageData constructor overload issues (ArrayBufferLike/SharedArrayBuffer).
    const imageData = ctx.createImageData(maxSize.width, maxSize.height);
    imageData.data.set(pixels);

    ctx.putImageData(imageData, 0, 0);
    return canvas.toDataURL() || '';
  }

  /**
   * A_25525 Der TI-M Client MUSS den Akteur darüber informieren, dass Dokumente Schadsoftware enthalten können
   */
  public async handleDownloadDialog(): Promise<boolean> {
    const downloadDialogData: DialogData = {
      title: 'DATEI HERUNTERLADEN',
      message:
        'Möchten Sie diese Datei herunterladen? ' +
        'Stellen Sie sicher, dass die Datei aus einer vertrauenswürdigen Quelle stammt.',
      confirmText: 'Herunterladen',
      cancelText: 'Abbrechen',
      svgIcon: 'circle-warning',
      cancelDataTestId: MediaTestIds.buttonCancelDownload,
      confirmDataTestId: MediaTestIds.buttonConfirmDownload,
      dialogContentTestId: MediaTestIds.dialogDownloadContent,
      dialogActionTestId: MediaTestIds.dialogDownloadActions
    };

    return firstValueFrom(this.dialogService.openWarningDialog(downloadDialogData, '28.6rem', '21.8rem'));
  }

  /**
   * A_25525 Der TI-M Client MUSS den Akteur darüber informieren, dass Dokumente Schadsoftware enthalten können
   */
  public async handleBulkDownloadDialog(fileCount: number): Promise<boolean> {
    const bulkDownloadDialogData: DialogData = {
      title: 'MEHRERE DATEIEN HERUNTERLADEN',
      message:
        `Möchten Sie ${fileCount} Dateien herunterladen?` +
        'Stellen Sie sicher, dass diese Dateien aus einer vertrauenswürdigen Quelle stammen',
      confirmText: 'Alle herunterladen',
      cancelText: 'Abbrechen',
      svgIcon: 'circle-warning',
      cancelDataTestId: MediaTestIds.buttonCancelBulkDownload,
      confirmDataTestId: MediaTestIds.buttonConfirmBulkDownload,
      dialogContentTestId: MediaTestIds.dialogBulkDownloadContent,
      dialogActionTestId: MediaTestIds.dialogBulkDownloadActions
    };

    return firstValueFrom(this.dialogService.openWarningDialog(bulkDownloadDialogData, '28.6rem', '21.8rem'));
  }

  protected async createThumbnail(
    element: HTMLImageElement,
    inputWidth: number,
    inputHeight: number,
    mimeType: string
  ): Promise<IThumbnailWithBlurhash> {
    let targetWidth = inputWidth;
    let targetHeight = inputHeight;

    if (targetHeight > this.MAX_HEIGHT) {
      targetWidth = Math.floor(targetWidth * (this.MAX_HEIGHT / targetHeight));
      targetHeight = this.MAX_HEIGHT;
    }
    if (targetWidth > this.MAX_WIDTH) {
      targetHeight = Math.floor(targetHeight * (this.MAX_WIDTH / targetWidth));
      targetWidth = this.MAX_WIDTH;
    }

    const canvas = new OffscreenCanvas(targetWidth, targetHeight);
    const context = canvas.getContext('2d') ?? document.createElement('canvas').getContext('2d')!;

    context.canvas.width = targetWidth;
    context.canvas.height = targetHeight;
    context.drawImage(element, 0, 0, targetWidth, targetHeight);

    const imageData = context.getImageData(0, 0, targetWidth, targetHeight);
    const blurhash = encode(imageData.data, imageData.width, imageData.height, 8, 6);

    const thumbnail = await this.createBlob(canvas, mimeType);
    return {
      info: {
        thumbnail_info: {
          w: targetWidth,
          h: targetHeight,
          mimetype: thumbnail.type,
          size: thumbnail.size
        },
        w: inputWidth,
        h: inputHeight,
        'tim_messenger.blurhash': blurhash
      },
      thumbnail
    };
  }

  private async createBlob(canvas: OffscreenCanvas | HTMLCanvasElement, mimeType: string): Promise<Blob> {
    if (canvas instanceof OffscreenCanvas) {
      return await canvas.convertToBlob({ type: mimeType });
    } else {
      return new Promise<Blob>((resolve, reject) => {
        canvas.toBlob(blob => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error('Fehler beim Erstellen des Blob'));
          }
        }, mimeType);
      });
    }
  }

  private loadImageElement(imageFile: File): Promise<{ width: number; height: number; img: HTMLImageElement }> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const objectUrl = URL.createObjectURL(imageFile);

      const cleanup = (): void => {
        try {
          URL.revokeObjectURL(objectUrl);
        } catch {
          // no Error
        }
        img.onload = null;
        img.onerror = null;
      };

      img.onload = (): void => {
        cleanup();
        resolve({
          width: img.width,
          height: img.height,
          img
        });
      };

      img.onerror = (): void => {
        cleanup();
        reject(new Error('Bild konnte nicht geladen werden'));
      };

      img.src = objectUrl;
    });
  }

  private updateFileStatus(fileName: string, status: 'active' | 'inactive'): void {
    if (!this.fileStatusSubjects.has(fileName)) {
      this.fileStatusSubjects.set(
        fileName,
        new BehaviorSubject({
          status,
          fileName
        })
      );
    } else {
      this.fileStatusSubjects.get(fileName)!.next({ status, fileName });
    }
  }

  private async checkImageFile(file: File | null): Promise<void> {
    if (!file) {
      this.snackbarService.error('Keine Datei ausgewählt.');
      throw new Error('Keine Datei ausgewählt.');
    }

    const isImageSmallEnough = await this.checkUploadSize(file);

    if (!isImageSmallEnough) {
      throw new Error('Image too big');
    }

    if (!file.type.startsWith('image/')) {
      this.snackbarService.error('Keine Bilddatei ausgewählt.');
      throw new Error('Keine Bilddatei ausgewählt.');
    }
  }

  private async checkUploadSize(file: File): Promise<boolean> {
    let MAX_UPLOAD_SIZE = 0;

    try {
      const config = await this.matrix.client.getMediaConfig();
      if (config['m.upload.size']) {
        MAX_UPLOAD_SIZE = config['m.upload.size'];
      }
      const maxUploadMB = (MAX_UPLOAD_SIZE / (1024 * 1024)).toFixed(2);
      const selectedFileSizeMB = (file.size / (1024 * 1024)).toFixed(2);

      if (file.size > MAX_UPLOAD_SIZE) {
        this.snackbarService.error(
          `Die Datei ist zu groß. Erlaubt sind max. ${maxUploadMB} MB. Deine Datei hat ${selectedFileSizeMB} MB.`
        );
        return false;
      }
      return true;
    } catch (error) {
      console.error('getMediaConfig fehlgeschlagen', error);

      if (!(error instanceof MatrixError)) {
        throw error;
      }
      throw matrixErrorAsJson('GetMediaConfigError', error);
    }
  }

  private async uploadContent(file: File): Promise<UploadResponse> {
    try {
      return await this.matrix.client.uploadContent(file);
    } catch (error) {
      console.error('uploadContent fehlgeschlagen', error);

      if (!(error instanceof MatrixError)) {
        throw error;
      }
      throw matrixErrorAsJson('UploadContentError', error);
    }
  }

  private async uploadUnencryptedFile(
    file: File,
    content: MediaEventContent,
    activeRoomId: string,
    isValid: boolean,
    sendAfterUpload: boolean
  ): Promise<void> {
    try {
      const uploadResponse = await this.uploadContent(file);
      content.url = uploadResponse.content_uri;
      this.updateFileStatus(file.name, isValid ? 'active' : 'inactive');
      if (sendAfterUpload && activeRoomId) {
        await this.sendMessageToServer(activeRoomId, content);
      }
    } catch (err) {
      this.snackbarService.error(`Upload fehlgeschlagen: ${err}`);
      console.error('Upload fehlgeschlagen:', err);
      throwError(() => err);
    } finally {
      this.setUploadEnabled(false);
    }
  }

  private async sendMessageToServer(roomId: string, content: MessageContent): Promise<void> {
    try {
      await this.messageService.sendMessage(roomId, content);
      this.snackbarService.success('Nachricht gesendet');
    } catch (err) {
      this.snackbarService.error(
        'Es kam beim Senden der Nachricht zu einem Fehler. Bitte versuchen Sie es später erneut.'
      );
      throw new Error('Messaghe could not be sent, when callind sendMessageToServer', { cause: err });
    }
  }

  private async encryptAttachement(data: ArrayBuffer): Promise<EncryptResult> {
    return await encrypt.encryptAttachment(data);
  }

  private async uploadEncryptedFile(
    file: File,
    content: MediaEventContent,
    activeRoomId: string,
    isValid: boolean,
    sendAfterUpload: boolean
  ): Promise<{ encryptedFile: EncryptedFile }> {
    this.setUploadEnabled(true);
    try {
      const arrayBuffer = await this.readFileAsArrayBuffer(file);
      const encryptResult = await this.encryptAttachement(arrayBuffer);
      const { content_uri } = await this.file.uploadEncryptedFileToServer(encryptResult);
      const result = {
        encryptedFile: encryptedFileMetadata(content_uri, encryptResult)
      };

      if (sendAfterUpload) {
        await this.attachAndSendEncryptedFileAsMessageToServer(file, content, result, activeRoomId, isValid);
      } else {
        content.file = result.encryptedFile;
      }
      return result;
    } catch (err) {
      this.setUploadEnabled(false);
      console.error('Fehler beim verschlüsselten Upload:', err);
      this.snackbarService.error(`Verschlüsselter Upload fehlgeschlagen`);

      throw err;
    } finally {
      setTimeout(() => {
        this.setUploadEnabled(false);
      }, 500);
    }
  }

  private async attachAndSendEncryptedFileAsMessageToServer(
    file: File,
    content: MediaEventContent,
    encryptedFile: { encryptedFile: EncryptedFile },
    activeRoomId: string,
    isValid: boolean
  ): Promise<void | Message> {
    if (content.msgtype === MsgType.Image && activeRoomId) {
      const imageInfo = await this.infoForImageFile(file);
      content.info = content.info || {};
      Object.assign(content.info, imageInfo);
      content.file = encryptedFile.encryptedFile;
      await this.sendMessageToServer(activeRoomId, content);
      this.updateFileStatus(file.name, isValid ? 'active' : 'inactive');
    } else {
      content.file = encryptedFile.encryptedFile;
      await this.sendMessageToServer(activeRoomId, content);
      this.updateFileStatus(file.name, isValid ? 'active' : 'inactive');
    }
  }

  private calculateMaxSize(originalWidth: number, originalHeight: number): { width: number; height: number } {
    const max = Math.max(originalWidth, originalHeight);
    if (max > 400) {
      const ratio = originalWidth / originalHeight;
      if (ratio >= 1) {
        return {
          width: 400,
          height: Math.round(400 / ratio)
        };
      } else {
        return { width: Math.round(400 * ratio), height: 400 };
      }
    } else {
      return { width: originalWidth, height: originalHeight };
    }
  }

  private validateFileName(filename: string): boolean {
    if (filename.length > 255 || filename.length === 0) {
      return false;
    }

    const parts = filename.split('.');
    if (parts.length > 2) {
      return false;
    }

    if (filename.includes('\0') || filename.includes('%00')) {
      return false;
    }

    return !(filename.includes('..') || filename.includes('/') || filename.includes('\\'));
  }
}

interface IThumbnail {
  info: ImageInfo;
  thumbnail: Blob;
}

type ImageInfoWithBlurhash = IThumbnail['info'] & {
  'tim_messenger.blurhash': string;
};

// DTO: IThumbnail mit erweitertem info
export interface IThumbnailWithBlurhash extends Omit<IThumbnail, 'info'> {
  info: ImageInfoWithBlurhash;
}

export interface IPreparedMedia extends IMediaObject {
  thumbnail?: IMediaObject;
}

export interface IMediaObject {
  mxc: string;
  file?: EncryptedFile;
}
